import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Box } from '@mui/material';

import { ErrorState } from './ErrorState';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional extra reset side-effect (e.g. a refetch) run when the user retries. */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors in its subtree and shows a recoverable error state
 * instead of unmounting the whole React tree to a blank white screen.
 *
 * Mount it around the routed content (keyed by location) so a crash in one page is
 * contained — the surrounding shell and navigation stay usable, and the user gets an
 * actionable message + retry instead of a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the failure for diagnostics (RUM/observability can hook in here later).
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <ErrorState
            title="Something went wrong"
            message={
              this.state.error.message || 'An unexpected error occurred while rendering this page.'
            }
            onRetry={this.handleReset}
            retryLabel="Reload section"
          />
        </Box>
      );
    }
    return this.props.children;
  }
}
